import addDays from 'date-fns/addDays';
import isBefore from 'date-fns/isBefore';
import { Observable } from 'rxjs';
import { combineLatestWith, map } from 'rxjs/operators';
import userAgent from '../../flows/purchase/functions/userAgent';
import type { BaseProfileItem, SearchApi } from '../../generated/api';
import { UserApi } from '../../generated/api';
import { Profile, ProfileUserType } from '../../models/ClientModels';
import { apiServerTimeToAppDate } from '../api-model-utils/apiServerTimeToAppDate';
import {
  currentUserId,
  currentUserProfile$,
  skipForUnauthenticatedUsers,
} from '../auth/utils';
import { kPrivateEmailPlaceholder } from '../constants';
import { firestore } from '../firebase/firebaseAccess';
import {
  collectionDataWithId,
  docDataWithIdMaybe,
  WithID,
} from '../firebase/rxfireExtensions';

export class ReferralServiceHelper {
  constructor(private searchSvc: SearchApi, private userSvc: UserApi) {}

  myReferer$: Observable<null | WithID<Profile>> = skipForUnauthenticatedUsers(
    uid => {
      // This source fill firestore faster, but can only respond the data
      // created by `ReferralServiceHelper#claimReferral` and campaign link, no
      // invite code
      const fasterRefererInfoSource = collectionDataWithId(
        firestore()
          .collection('privateProfiles')
          .doc(uid)
          .collection('invitedBy')
          // @xin promised `invitedBy` collection will always only have one record
          .limit(1),
      ).pipe(map(refererRecords => refererRecords[0] ?? null));

      // This is the source of truth, but a little slow, may take 1 second to
      // process data and fill in firestore
      const sourceOfTruth = docDataWithIdMaybe(
        firestore().collection('userStats').doc(uid),
      ).pipe(
        map((stats): null | WithID<Profile> =>
          stats?.referrerUserId == null
            ? null
            : {
                id: stats.referrerUserId,
                ...stats.referrerUserProfile!,
              },
        ),
      );

      return sourceOfTruth.pipe(
        combineLatestWith(fasterRefererInfoSource),
        map(([sourceOfTruth, fasterRefererInfoSource]) =>
          sourceOfTruth != null ? sourceOfTruth : fasterRefererInfoSource,
        ),
      );
    },
    null,
  );

  canIStillClaimNewUserReferral$: Observable<boolean> =
    skipForUnauthenticatedUsers(
      () =>
        currentUserProfile$.pipe(
          map(profile =>
            Boolean(
              profile?.createdAt &&
                // (today - createDay) < 7 => today < createDay + 7
                isBefore(new Date(), addDays(profile.createdAt.toDate(), 7)),
            ),
          ),
        ),
      false,
    );

  async claimReferral(shareLinkFromReferer: string): Promise<void> {
    const currUserId = currentUserId();
    if (!currUserId) return;
    await this.userSvc.userApiControllerExecuteClaimAction({
      userId: currUserId,
      executeClaimActionRequest: {
        url: shareLinkFromReferer,
        userAgent: userAgent(),
      },
    });
  }
}

export function profileItemToClientProfile(
  profileItem: BaseProfileItem,
): Profile {
  return {
    ...profileItem,
    email: kPrivateEmailPlaceholder,
    userType: profileItem.userType as undefined | ProfileUserType,
    storeType: profileItem.storeType as any,
    createdAt:
      profileItem.createdAt == null
        ? undefined
        : apiServerTimeToAppDate(profileItem.createdAt),
  };
}
